import React, {useCallback, useContext, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box, Button, Flex, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {useCommittedCoverVariantProduct} from '@amblique/king-living/app/hooks/use-committed-cover-variant-product';
import {buildSteps, isCustomizeJourneyComplete} from '@amblique/king-living/app/hooks/use-customize-steps';
import {
  buildChildCartItems,
  buildCustomizeAtcSuccessPayload,
  buildPrimaryCartItem,
  computeCustomizeTotal,
  computeCustomizeListTotal,
  requiresCoverForConfiguredImage,
  resolveCustomizeAtcConfiguredImageUrl,
  resolveCustomizeAtcDisplayPrice,
  resolveKingGuardSourceProduct,
} from '@amblique/king-living/app/utils/cart-utils';
import {getCoverGroups} from '@amblique/king-living/app/utils/customize-selections';
import {applyFinishedGoodAtcOverride} from '@amblique/king-living/app/utils/inventory-utils';
import {useCommittedConfiguredImageUrl} from '@amblique/king-living/app/hooks/use-committed-configured-image-url';
import {
  PDP_PRICE_BREAKDOWN_SCROLL_OPTIONS,
  scrollElementBelowStickyHeader,
} from '@amblique/king-living/app/utils/utils';
import {useInPageCtaVisibility} from '@amblique/king-living/app/hooks/use-in-page-cta-visibility';
import {usePdpAtcStripHeight} from '@amblique/king-living/app/hooks/use-pdp-atc-strip-height';
import {AddToCartStripChevronIcon} from '@amblique/king-living/app/components/icons';
import ProductStockIndicator from '@amblique/king-living/app/components/product-stock-indicator';
import {CustomizeJourneyContext} from '@amblique/king-living/app/context/customize-journey-context';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {buildSelectionSummaryText} from '@amblique/king-living/app/utils/build-selection-summary-text';
import BazaarvoiceInlineRating from '@amblique/king-living/app/components/bazaarvoice/inline-rating';
import {BAZAARVOICE_REVIEWS_SECTION_ID} from '@amblique/king-living/app/utils/bazaarvoice';

const PDP_PRICE_SECTION_ID = 'pdp-price-section';

/**
 * Floating Add-to-Cart strip for the PDP.
 *
 * Complex strip states:
 * - **Desktop:** line 2 shows selected option / add-on names (or “No selection yet”); partial
 *   selections visible while ATC is disabled.
 * - **Mobile:** product name + From/price/chevron only — no options summary line (FSD).
 * - **Disabled** (incomplete config): ATC disabled until set/customize is complete.
 * - **Enabled** (complete): ATC enabled.
 *
 * `ctaElement` (preferred) or legacy `ctaRef` must point at the **primary in-page Add to Cart
 * button**. The floating strip is **shown by default** and only **hidden** when that button’s top is both below the sticky chrome **and** inside
 * the viewport (user can see it). If the button is below the fold or covered by the header/menu,
 * the strip stays visible.
 */
function AddToCartStrip({
  product,
  variant,
  shopperInventoryIds,
  isComplexProduct,
  hasConfigurableOptions = false,
  isSimpleFreeForm = false,
  linkedFinishedGoodProduct = null,
  notifyMeMode = false,
  onNotifyMeClick,
  isCtaPending = false,
  isCtaLoading = false,
  currency,
  isBasketLoading,
  ctaRef,
  ctaElement = null,
  addCustomizeToCart,
  onSuccess,
  priceData,
  disableButton,
  onAddToCart,
  isConfigurationSelectorOpen = false,
}) {
  // KINDW-1119: Simple PDPs with configurable options share the customize-aware
  // pricing/CTA logic with Complex PDPs (selections drive total + multi-line basket).
  // KINDW-1121: Simple Freeform (variation-base master) also uses the customize path so
  // cover selection drives the total + the primary line item.
  const useCustomizePath = isComplexProduct || hasConfigurableOptions || isSimpleFreeForm;
  const intl = useIntl();
  const showToast = useToast();
  const styles = useMultiStyleConfig('ProductDetail', {});
  const desktopStripRef = useRef(null);
  const mobileStripRef = useRef(null);

  usePdpAtcStripHeight(desktopStripRef, mobileStripRef, Boolean(product));

  // ── true = hide floating strip (in-page ATC fully below header — no duplicate needed) ──
  const hideFloatingStrip = useInPageCtaVisibility(ctaElement ?? ctaRef?.current ?? null);

  // ── Complex product: selections, price, completion, handler ──
  const ctx = useContext(CustomizeJourneyContext);
  const committedSelection = ctx?.committedSelection;
  const flyoutDraftState = ctx?.flyoutDraftState;
  const inlineSelectionsDraft = ctx?.inlineSelectionsDraft;
  const resetJourney = ctx?.resetJourney;
  const setProductsMap = ctx?.setProductsMap;
  const setOptionPriceMap = ctx?.setOptionPriceMap;
  const setOptionListPriceMap = ctx?.setOptionListPriceMap;
  const coverVariant = useCommittedCoverVariantProduct(product);

  // Pricing base: cover variant for Complex (variant-level price); master for Simple PDP.
  const customizeBaseProduct = isComplexProduct ? coverVariant : product;

  // KingGuard option data: prefer the cover variant when it has been enriched with
  // per-variant `options` (SFRA `c_allVariantsData`); fall back to the master.
  // Resolved separately from `customizeBaseProduct` so Simple PDPs (which keep the
  // master as their base for cover-less pricing) still read the variant-level
  // KingGuard price the user saw in the checkbox. See bug analysis: previously
  // the Simple PDP total dropped the KingGuard price because the master's option
  // model misses the per-variant overrides.
  const kingGuardSourceProduct = useMemo(
    () => (useCustomizePath ? resolveKingGuardSourceProduct(product, coverVariant) : null),
    [useCustomizePath, coverVariant, product]
  );

  /**
   * Summary line must match what the user sees in the configure UI:
   * - Flyout/drawer open → live draft (not yet committed on Done)
   * - Inline shell (when mounted) → live inline draft
   * - Otherwise → committed selection
   */
  const selectionsForSummary = useMemo(() => {
    if (flyoutDraftState?.isOpen) {
      return flyoutDraftState.draftSelections ?? null;
    }

    if (inlineSelectionsDraft != null) {
      return inlineSelectionsDraft;
    }

    return committedSelection?.selections ?? null;
  }, [
    flyoutDraftState?.isOpen,
    flyoutDraftState?.draftSelections,
    inlineSelectionsDraft,
    committedSelection?.selections,
  ]);

  const complexTotal = useMemo(
    () => (useCustomizePath ? computeCustomizeTotal(committedSelection?.selections, customizeBaseProduct, true, kingGuardSourceProduct, setOptionPriceMap) : 0),
    [useCustomizePath, committedSelection?.selections, customizeBaseProduct, kingGuardSourceProduct, setOptionPriceMap]
  );

  const complexListTotal = useMemo(
    () => (useCustomizePath ? computeCustomizeListTotal(committedSelection?.selections, customizeBaseProduct, true, kingGuardSourceProduct, setOptionListPriceMap) : 0),
    [useCustomizePath, committedSelection?.selections, customizeBaseProduct, kingGuardSourceProduct, setOptionListPriceMap]
  );

  // Parent passes authoritative display price via `priceData` for Simple customize flows
  // (e.g. CMTO min from `c_priceProcessed` before a cover is selected). Complex PDPs must
  // keep using locally computed `complexTotal` (cover-variant base) — parent `priceData`
  // is the master starting price, not the configured total.
  // Declared before `handleComplexClick` — that callback closes over this value (TDZ crash
  // if declared later → Contentful ProductDetail error boundary blanks the PDP).
  const effectivePrice = useCustomizePath
    ? (!isComplexProduct && priceData?.currentPrice != null ? priceData.currentPrice : complexTotal)
    : (priceData?.currentPrice ?? 0);

  const steps = useMemo(() => {
    if (!useCustomizePath || !product) return [];

    if (committedSelection?.mode === 'set' && committedSelection?.productId) {
      const linkedProduct = setProductsMap?.[committedSelection.productId];

      if (linkedProduct) return buildSteps(product);
    }

    return buildSteps(product, !isComplexProduct ? {isSimplePDP: true} : undefined);
  }, [useCustomizePath, isComplexProduct, product, committedSelection?.mode, committedSelection?.productId, setProductsMap]);

  const isSimplePdpFlow = !isComplexProduct && hasConfigurableOptions;
  const atcCoverGroups = useMemo(() => getCoverGroups(product), [product]);

  const isComplexComplete = useMemo(() => {
    if (!useCustomizePath || !product) return false;

    return isCustomizeJourneyComplete(committedSelection?.selections, steps, {
      coverGroups: atcCoverGroups,
    });
  }, [useCustomizePath, product, committedSelection?.selections, steps, atcCoverGroups]);

  const optionsSummary = useMemo(
    () => (useCustomizePath ? buildSelectionSummaryText(selectionsForSummary) : ''),
    [useCustomizePath, selectionsForSummary]
  );

  const requireCoverForImage = requiresCoverForConfiguredImage({
    committedMode: committedSelection?.mode,
    isSimplePdpFlow,
  });

  const configuredHeroImageUrl = useCommittedConfiguredImageUrl(product, {
    enabled: useCustomizePath,
    requireCover: requireCoverForImage,
  });

  // Stored on the line item JSON (c_selectedOptionsUI.cylindoUrl) and read downstream
  // as the line-item thumbnail (cart flyout, upsell, checkout, order confirmation, emails).
  //
  // Predefined SETS build the configured Cylindo/PIM hero here — the SAME resolver as
  // Customize Your Own — instead of the PDP set-card swatch. The set swatch
  // (c_pimlyConfigurationImage[0]) is a PDP-only presentation image; carts and all
  // downstream surfaces must show the actual configured product image. `requireCover`
  // is already false for set mode (requiresCoverForConfiguredImage) so the hero resolves
  // without a cover slot. This matches ComplexAddToCartButton, which never applied a set
  // swatch override.
  const cylindoUrl = configuredHeroImageUrl;

  const {primaryItem, childItems} = useMemo(
    () => {
      if (!useCustomizePath) {
        return {primaryItem: null, childItems: []};
      }

      const base = buildPrimaryCartItem(
        committedSelection?.selections,
        kingGuardSourceProduct,
        product,
        cylindoUrl,
        committedSelection,
        setProductsMap
      );
      const baseChildren = buildChildCartItems(
        committedSelection?.selections,
        product?.id ?? product?.productId ?? null
      );

      // Same Family A FG swap as ComplexAddToCartButton (floating strip was missing this).
      return applyFinishedGoodAtcOverride({
        product,
        selections: committedSelection?.selections,
        primaryItem: base,
        childItems: baseChildren,
        inventoryIdsFromCookie: shopperInventoryIds,
        liveFgProduct: linkedFinishedGoodProduct,
      });
    },
    [
      useCustomizePath,
      committedSelection,
      committedSelection?.selections,
      kingGuardSourceProduct,
      product,
      cylindoUrl,
      setProductsMap,
      shopperInventoryIds,
      linkedFinishedGoodProduct,
    ]
  );

  const handleComplexClick = useCallback(async () => {
    if (disableButton || !isComplexComplete || !primaryItem) return;

    try {
      const result = await addCustomizeToCart(primaryItem, childItems, {
        displayTotal: effectivePrice,
        optionPriceMap: setOptionPriceMap,
      });

      if (result) {
        const configuredImageUrl = resolveCustomizeAtcConfiguredImageUrl({
          masterProduct: product,
          selections: committedSelection?.selections,
          primaryItem,
          requireCover: requireCoverForImage,
        });

        onSuccess(buildCustomizeAtcSuccessPayload({
          product,
          configuredImageUrl,
          primaryProductId: primaryItem.productId,
          total: effectivePrice,
          listTotal: complexListTotal,
          totalQty: 1 + childItems.length,
          selections: committedSelection?.selections,
          committedSelection,
        }));
        resetJourney?.();
      }
    } catch {
      showToast({title: intl.formatMessage(API_ERROR_MESSAGE), status: 'error'});
    }
  }, [
    disableButton,
    isComplexComplete,
    primaryItem,
    childItems,
    addCustomizeToCart,
    product,
    committedSelection,
    committedSelection?.selections,
    requireCoverForImage,
    onSuccess,
    resetJourney,
    showToast,
    intl,
    effectivePrice,
    complexListTotal,
    setOptionPriceMap
  ]);

  // KINDW-1121 / FSD §1.3 — when the selected cover (or the whole master when nothing is
  // in stock) is OOS, swap the CTA into a "Notify Me" button that is always enabled.
  const isDisabled = notifyMeMode
    ? isCtaPending
    : (useCustomizePath ? !isComplexComplete || isCtaPending || disableButton : disableButton);

  const handleClick = notifyMeMode
    ? onNotifyMeClick
    : (useCustomizePath ? handleComplexClick : onAddToCart);

  /** Desktop line 2 only: partial selections (add-ons, flyout draft, committed slots). */
  const desktopStripOptionsContent = useMemo(() => {
    if (!useCustomizePath) return null;

    if (optionsSummary) return optionsSummary;

    return (
      <FormattedMessage
        defaultMessage="No selection yet"
        id="add_to_cart_strip.label.no_selection_yet"
      />
    );
  }, [useCustomizePath, optionsSummary]);

  const formattedPrice = effectivePrice > 0
    ? formatCurrencyHideTrailingZeroCents(intl, effectivePrice, currency)
    : '';

  const stripDisplayPriceValue = resolveCustomizeAtcDisplayPrice({
    complexTotal: !isComplexProduct && priceData?.currentPrice != null ? priceData.currentPrice : complexTotal,
    masterProductPrice: product?.price ?? 0,
    nonCustomizePrice: priceData?.currentPrice ?? 0,
    useCustomizePath,
  });

  const stripFormattedPrice =
    stripDisplayPriceValue > 0
      ? formatCurrencyHideTrailingZeroCents(intl, stripDisplayPriceValue, currency)
      : '';

  /** Mobile customize strip: prefix price with “From” whenever a price line is shown (desktop uses “Total”). */
  const showFromOnMobileComplexStrip = useCustomizePath && Boolean(stripFormattedPrice);

  /** Non-customize mobile: “From” when showing master starting price before selection. */
  const showFromPrefix = useCustomizePath && !isComplexComplete && product?.price > 0;
  const mobilePrice = showFromPrefix
    ? formatCurrencyHideTrailingZeroCents(intl, product.price, currency)
    : formattedPrice;

  const productName = product?.name || '';

  /** Locale JSON often has `"message": ""` which compiles to an empty string — still show “From”. */
  const mobileFromLabel = useMemo(() => {
    const t = intl.formatMessage({
      defaultMessage: 'From',
      id: 'add_to_cart_strip.label.from_prefix',
    });

    return t?.trim() ? t : 'From';
  }, [intl]);

  const handleScrollToPrice = useCallback(() => {
    const scrollOpts = PDP_PRICE_BREAKDOWN_SCROLL_OPTIONS;

    if (isComplexProduct) {
      if (committedSelection?.mode === 'customize') {
        scrollElementBelowStickyHeader('#customize-your-own-anchor', undefined, scrollOpts);

        return;
      }

      scrollElementBelowStickyHeader(
        ['#choose-your-sets-anchor', '#customize-your-own-anchor'],
        undefined,
        scrollOpts
      );

      return;
    }

    scrollElementBelowStickyHeader(
      ['.cover-swatch-grid', '.customize-steps-inline-content'],
      undefined,
      scrollOpts
    );
  }, [committedSelection, isComplexProduct]);

  // Don't render until we have a product
  if (!product) return null;

  // When a right-side flyout/panel is open, hide the floating strip so it does not
  // overlap the gallery or the panel footer (panel carries its own Done/Next actions).
  const suppressFloatingStrip = hideFloatingStrip || isConfigurationSelectorOpen;

  return (
    <>
      {/* Desktop strip — fixed bottom */}
      <Flex
        {...styles.addToCartStripDesktop}
        ref={desktopStripRef}
        data-testid="add-to-cart-strip-desktop"
        opacity={suppressFloatingStrip ? 0 : 1}
        pointerEvents={suppressFloatingStrip ? 'none' : 'auto'}
        {...(isConfigurationSelectorOpen ? {zIndex: -1} : {})}
        transform={suppressFloatingStrip ? 'translateY(100%)' : 'translateY(0)'}
        transition="transform 0.2s ease, opacity 0.2s ease"
      >
        <Flex {...styles.addToCartStripDesktopInner}>
          <Box flex="1" minW={0}>
            <Flex
              align="center"
              gap="16px"
              mb={useCustomizePath ? 0.5 : 0}
              maxW="100%"
              minW={0}
            >
              <Text
                {...styles.addToCartStripProductName}
                flex="0 1 auto"
                minW={0}
              >
                {productName}
              </Text>
              <Box flexShrink={0}>
                <BazaarvoiceInlineRating
                  product={product}
                  redirectUrl={`#${BAZAARVOICE_REVIEWS_SECTION_ID}`}
                  variant="strip"
                />
              </Box>
            </Flex>
            {desktopStripOptionsContent != null && (
              <Text {...styles.addToCartStripOptions}>
                {desktopStripOptionsContent}
              </Text>
            )}
          </Box>

          <Flex align="center" flexShrink={0} gap="24px">
            {stripFormattedPrice && (
              <Box className="add-to-cart-strip-price-container" textAlign="right">
                <Text {...styles.addToCartStripPrice}>
                  <FormattedMessage
                    defaultMessage="Total {price}"
                    id="add_to_cart_strip.label.total_price"
                    values={{price: stripFormattedPrice}}
                  />
                </Text>
                {!isComplexProduct && (
                  <ProductStockIndicator
                    indicatorVariant="strip"
                    product={product}
                    shopperInventoryIds={shopperInventoryIds}
                    variant={variant}
                    onNotifyMeClick={onNotifyMeClick}
                  />
                )}
              </Box>
            )}
            <Button
              {...styles.addToCartStripButton}
              isDisabled={isDisabled}
              isLoading={isCtaLoading}
              onClick={handleClick}
            >
              {notifyMeMode ? (
                <FormattedMessage
                  defaultMessage="Notify Me"
                  id="add_to_cart_strip.button.notify_me"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Add to cart"
                  id="add_to_cart_strip.button.add_to_cart"
                />
              )}
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {/* Mobile strip — fixed bottom */}
      <Box
        {...styles.addToCartStripMobile}
        ref={mobileStripRef}
        data-testid="add-to-cart-strip-mobile"
        opacity={suppressFloatingStrip ? 0 : 1}
        pointerEvents={suppressFloatingStrip ? 'none' : 'auto'}
        {...(isConfigurationSelectorOpen ? {zIndex: -1} : {})}
        transform={suppressFloatingStrip ? 'translateY(100%)' : 'translateY(0)'}
        transition="transform 0.2s ease, opacity 0.2s ease"
      >
        <Flex {...styles.addToCartStripMobileInner}>
          <Box {...styles.addToCartStripMobileTextBlock}>
            <Text {...styles.addToCartStripMobileProductName}>
              {productName}
            </Text>
            <BazaarvoiceInlineRating
              product={product}
              redirectUrl={`#${BAZAARVOICE_REVIEWS_SECTION_ID}`}
              variant="strip"
            />
            {useCustomizePath && stripFormattedPrice && (
              <Flex {...styles.addToCartStripMobilePriceRowWithChevron}>
                {showFromOnMobileComplexStrip ? (
                  <Text as="span" {...styles.addToCartStripMobileFromPrefix}>
                    {mobileFromLabel}
                  </Text>
                ) : (
                  <Box aria-hidden minW={0} />
                )}
                <Box {...styles.addToCartStripMobilePriceRemainder}>
                  <Flex {...styles.addToCartStripMobilePriceChevronCluster}>
                    <Text {...styles.addToCartStripMobilePriceInCluster}>
                      {stripFormattedPrice}
                    </Text>
                    <Box
                      flexShrink={0}
                      marginLeft="8px"
                    >
                      <Box
                        as="button"
                        type="button"
                        {...styles.addToCartStripChevron}
                        aria-label={intl.formatMessage({
                          defaultMessage: 'View price breakdown',
                          id: 'add_to_cart_strip.button.view_price_breakdown',
                        })}
                        onClick={handleScrollToPrice}
                      >
                        <AddToCartStripChevronIcon aria-hidden boxSize="100%" />
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            )}
            {!useCustomizePath && (mobilePrice || showFromPrefix) && (
              <Text {...styles.addToCartStripMobilePrice}>
                {showFromPrefix && (
                  <Box as="span" display="inline" marginRight="4px">
                    <FormattedMessage
                      defaultMessage="From"
                      id="add_to_cart_strip.label.from_prefix"
                    />
                  </Box>
                )}
                {mobilePrice}
              </Text>
            )}
          </Box>

          <Flex align="center" flexShrink={0} gap={2}>
            <Button
              {...styles.addToCartStripButton}
              isDisabled={isDisabled}
              isLoading={isCtaLoading}
              onClick={handleClick}
            >
              {notifyMeMode ? (
                <FormattedMessage
                  defaultMessage="Notify Me"
                  id="add_to_cart_strip.button.notify_me"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Add to cart"
                  id="add_to_cart_strip.button.add_to_cart"
                />
              )}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </>
  );
}

AddToCartStrip.propTypes = {
  product: PropTypes.object,
  variant: PropTypes.object,
  shopperInventoryIds: PropTypes.string,
  isComplexProduct: PropTypes.bool,
  hasConfigurableOptions: PropTypes.bool,
  isSimpleFreeForm: PropTypes.bool,
  linkedFinishedGoodProduct: PropTypes.object,
  notifyMeMode: PropTypes.bool,
  onNotifyMeClick: PropTypes.func,
  isCtaPending: PropTypes.bool,
  isCtaLoading: PropTypes.bool,
  currency: PropTypes.string,
  isBasketLoading: PropTypes.bool,
  ctaRef: PropTypes.object,
  ctaElement: PropTypes.object,
  addCustomizeToCart: PropTypes.func,
  onSuccess: PropTypes.func,
  priceData: PropTypes.object,
  disableButton: PropTypes.bool,
  onAddToCart: PropTypes.func,
  isConfigurationSelectorOpen: PropTypes.bool,
};

export {PDP_PRICE_SECTION_ID};
export default AddToCartStrip;
